/**
 * VerifiedLeadsPage — verified pool, ready for team assignment.
 *
 * Apr 26, 2026 (Squad-B). Stub UI; backend endpoint may not exist yet.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, ArrowLeft } from 'lucide-react';
import { safeGet } from '@/pages/dashboard/roles/_dashboardShared';

interface LeadRow {
  id: string;
  name?: string | null;
  customerName?: string | null;
  contactPhone?: string | null;
  phone?: string | null;
  source?: string | null;
  productInterest?: string | null;
  city?: string | null;
  teamId?: string | null;
  verificationStatus?: string | null;
  createdAt?: string | null;
}

function leadName(l: LeadRow): string {
  return l.customerName || l.name || 'Unknown lead';
}

export function VerifiedLeadsPage() {
  const [rows, setRows] = useState<LeadRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    safeGet<{ items?: LeadRow[]; data?: LeadRow[] } | LeadRow[]>(
      '/leads?verificationStatus=verified&unassigned=true&limit=50',
    ).then((r) => {
      const raw = r.data;
      const list: LeadRow[] = Array.isArray(raw)
        ? raw
        : (raw as { items?: LeadRow[]; data?: LeadRow[] })?.items
        ?? (raw as { items?: LeadRow[]; data?: LeadRow[] })?.data
        ?? [];
      setRows(list.filter((l) => !l.teamId));
      setError(r.error);
      setLoading(false);
    });
  }, []);

  return (
    <div className="aura-root" style={{ padding: 24, background: 'var(--ivory-0)', minHeight: '100%' }}>
      <div style={{ marginBottom: 18, display: 'flex', alignItems: 'center', gap: 12 }}>
        <Link
          to="/dashboard"
          style={{
            color: 'var(--ink-3)',
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 12,
          }}
        >
          <ArrowLeft size={12} /> Back
        </Link>
      </div>

      <div style={{ marginBottom: 18 }}>
        <h2
          style={{
            fontFamily: 'var(--ff-serif)',
            fontSize: 22,
            fontWeight: 500,
            marginBottom: 2,
            color: 'var(--ink-0)',
          }}
        >
          Verified pool
        </h2>
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
          Verified leads awaiting team assignment.
        </div>
      </div>

      <div
        style={{
          background: 'var(--ivory-1)',
          border: '1px solid var(--line)',
          borderRadius: 10,
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <div style={{ padding: 28, fontSize: 13, color: 'var(--ink-3)', textAlign: 'center' }}>
            Loading…
          </div>
        ) : error || rows.length === 0 ? (
          <div style={{ padding: 36, textAlign: 'center' }}>
            <Users size={28} style={{ color: 'var(--ink-3)', marginBottom: 8 }} />
            <div style={{ fontSize: 13, color: 'var(--ink-2)', fontWeight: 500, marginBottom: 4 }}>
              {error ? 'Verified pool not available yet' : 'No verified leads waiting'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', maxWidth: 360, margin: '0 auto' }}>
              {error
                ? 'Backend endpoint is not online. Once Squad-A ships the schema, verified leads will appear here.'
                : 'Verify leads from the inbox — they will land here ready for team assignment.'}
            </div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--ivory-2)', borderBottom: '1px solid var(--line)' }}>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 500, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                  Customer
                </th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 500, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                  Product interest
                </th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 500, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                  City
                </th>
                <th style={{ padding: '10px 16px', textAlign: 'right', fontSize: 11, fontWeight: 500, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((lead) => (
                <tr key={lead.id} style={{ borderBottom: '1px solid var(--line)' }}>
                  <td style={{ padding: '10px 16px', color: 'var(--ink-0)', fontWeight: 500 }}>
                    {leadName(lead)}
                  </td>
                  <td style={{ padding: '10px 16px', color: 'var(--ink-2)' }}>
                    {lead.productInterest ?? '—'}
                  </td>
                  <td style={{ padding: '10px 16px', color: 'var(--ink-2)' }}>
                    {lead.city ?? '—'}
                  </td>
                  <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                    <button
                      type="button"
                      className="btn btn-outline"
                      style={{ fontSize: 11, padding: '4px 10px' }}
                      onClick={() => {
                        // TODO: open team-picker drawer once backend ships PATCH /leads/:id/assign-team.
                        alert(`Assign lead ${lead.id} to team — picker coming soon`);
                      }}
                    >
                      Assign to team
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default VerifiedLeadsPage;
